"use client"

import { motion, useReducedMotion } from "framer-motion"
import { useTranslations } from "next-intl"
import { Store } from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { Link } from "@/i18n/navigation"
import { DynamicLucideIcon } from "@/components/icons/dynamic-lucide-icon"
import { useMainCategoriesWithCounts } from "@/app/[locale]/categories/_api/use-category-queries"
import { AnimatedSection } from "../animated-section"

type Category = { id: string; name: string; icon?: string; color: string }

function useCats() {
  const { data, isLoading } = useMainCategoriesWithCounts()
  const categories: Category[] = (data?.categories ?? []).map((c) => ({ id: c.id, name: c.name, icon: c.icon ?? undefined, color: c.color }))
  return { categories, isLoading }
}

function TileMarquee({ items, direction }: { items: Category[]; direction: "left" | "right" }) {
  const reduce = useReducedMotion()
  const tile = (c: Category, key: string) => (
    <Link key={key} href={`/shops?category=${c.id}`}>
      <div
        className="flex h-24 w-36 shrink-0 flex-col items-center rounded-2xl pt-5 transition-all hover:-translate-y-1 hover:shadow-lg"
        style={{ backgroundColor: c.color }}
      >
        <span className="flex h-7 w-7 items-center justify-center">
          {c.icon ? (
            <DynamicLucideIcon name={c.icon} size={28} className="text-white" />
          ) : (
            <Store className="h-7 w-7 text-white" />
          )}
        </span>
        <span className="mt-2 w-full truncate px-3 text-center text-sm font-medium text-white">{c.name}</span>
      </div>
    </Link>
  )
  if (reduce) {
    return <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{items.map((c) => tile(c, c.id))}</div>
  }
  const doubled = [...items, ...items]
  return (
    // `overflow-x-clip` leaves the y-axis visible (unlike `overflow-hidden`); the
    // `py-3` keeps the hover-lifted tile inside the masked box so the mask doesn't
    // clip its top.
    <div
      className="overflow-x-clip py-3"
      style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}
    >
      <motion.div
        className="flex w-max gap-4"
        animate={{ x: direction === "left" ? ["0%", "-50%"] : ["-50%", "0%"] }}
        transition={{ duration: 40, ease: "linear", repeat: Infinity }}
      >
        {doubled.map((c, i) => tile(c, `${c.id}-${i}`))}
      </motion.div>
    </div>
  )
}

/** spotlight — two-row scrolling marquee of solid-color category tiles, opposite directions, edge fade, full-width. */
export function CategoriesSpotlight() {
  const t = useTranslations("start.categories")
  const { categories, isLoading } = useCats()
  const mid = Math.ceil(categories.length / 2)
  return (
    <section className="py-16">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection>
            <div className="mb-10 text-center">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl">{t("title")}</h2>
              <p className="mx-auto max-w-2xl text-lg text-muted-foreground">{t("sub")}</p>
            </div>
          </AnimatedSection>
        </div>
      </div>
      {/* Marquee at max-w-7xl to match hero rails. Second row sits above the first
          (z-10) so a hover-lifted tile overlaps row 1 without shrinking the gap. */}
      {isLoading ? (
        <div className="container mx-auto px-4"><Skeleton className="h-56 rounded-2xl" /></div>
      ) : (
        <div className="mx-auto max-w-7xl">
          <div className="relative z-0">
            <TileMarquee items={categories.slice(0, mid)} direction="left" />
          </div>
          <div className="relative z-10">
            <TileMarquee items={categories.slice(mid)} direction="right" />
          </div>
        </div>
      )}
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-7xl">
          <AnimatedSection delay={0.2}>
            <div className="mt-10 text-center">
              <Link href="/shops" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
                {t("browse")} &rarr;
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}
